import { useState, useEffect } from "react";
import { Box, Button, Typography, Switch, Select, MenuItem } from '@mui/material';
import BoilerModal from './BoilerModal';
import BoilerGraph, { KEY_UNIT_MAP } from './BoilerGraph';
import BoilerPanel from './BoilerPanel';
import BoilerToolbar from './BoilerToolbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { BOILER_TEMP } from "../../../../constants";
import FileSaver from "file-saver";
import useAxiosProtect from "../../../../hooks/useAxiosProtect";

const ECON_TYPE_KEY = {
  'Boiler Makeup Water': 'bmw',
  'Boiler Feedwater': 'bfw',
  'Process Stream': 'ps',
};

const getPFDImageKey = (config) => {
  if (!config) return null;
  const econ = config.economizer === 'Yes' ? (ECON_TYPE_KEY[config.economizerType] ?? null) : 'n';
  if (!econ) return null;
  const bcw = config.blowdownCoolingWater === 'Yes' ? 'y' : 'n';
  const cr = config.condensateReturn === 'Yes' ? 'y' : 'n';
  const dsi = config.deaeratorSteamInjection === 'Yes' ? 'y' : 'n';
  return `${econ}_${bcw}_${cr}_${dsi}`;
};

const KEY_TO_ECON_TYPE = Object.fromEntries(
  Object.entries(ECON_TYPE_KEY).map(([label, key]) => [key, label])
);


const computeBoilerPanelFlags = (boiler) => {
  if (!boiler) return { disabledFields: [] };
  const econType = boiler.economizer_type;
  const disabledFields = [];

  if (econType !== 'ps') {
    disabledFields.push('preheatWater');
  }
  if (boiler.cooling_water === false) {
    disabledFields.push('coolingWater');
  }
  if (econType === null || econType === undefined) {
    disabledFields.push('inletTemp', 'outletTemp', 'energyRecovery');
  }
  return { disabledFields };
};


const mapApiBoilerToConfig = (boiler) => {
  if (!boiler) return null;
  const econKey = (boiler.economizer_type || '').trim();
  const hasEcon = econKey in KEY_TO_ECON_TYPE;

  return {
    id: boiler.id,
    economizer: hasEcon ? 'Yes' : 'No',
    economizerType: hasEcon ? KEY_TO_ECON_TYPE[econKey] : null,
    blowdownCoolingWater: boiler.cooling_water ? 'Yes' : 'No',
    condensateReturn: boiler.condensate_return ? 'Yes' : 'No',
    deaeratorSteamInjection: boiler.steam_injection ? 'Yes' : 'No',
    boilerMakeupWaterTemp: boiler.boiler_water_temp ?? '',
    feedwaterTemp: boiler.feedwater_temp ?? '',
    heatCapacity: boiler.heat_capacity ?? '',
    blowdownRate: boiler.blowdown_rate ?? '',
    blowdownFlashSteamRate: boiler.blowdown_steam_rate ?? '',
    condensateFlashSteamRate: boiler.condensate_steam_rate ?? '',
    blowdownWaterTemp: boiler.blowdown_temp ?? '',
    daVentSystem: boiler.vent_system ?? '',
    steamPressure: boiler.steam_pressure ?? '',
    condensateReturnTemp: boiler.condensate_return_temp ?? '',
    diagram: boiler.diagram ?? null,
  };
};

const SUMMARY_FIELDS = [
  { label: 'Boiler Input', key: 'boilerInput', value: 5436, unit: 'MBTU' },
  { label: 'Steam Energy', key: 'energyInputOfSteam', value: 2823, unit: 'MBTU' },
  { label: 'Steam Load', key: 'steamLoad', value: 2236, unit: 'lbs' },
  { label: 'Heat Recovery', key: 'energyRecovery', value: 290, unit: 'MBTU' },
  { label: 'Heat Losses', key: 'heatLosses', value: 365, unit: 'MBTU' },
  { label: 'Boiler Efficiency', key: 'boilerEfficiency', percentage: true },
  { label: 'System Efficiency', key: 'systemEfficiency', percentage: true },
];

const ENERGY_CONVERSION = {
  MBTU: 1,
  BTU: 1000,
  MMBTU: 0.001,
  kWh: 1000 / 3412.142,
};

const MASS_FLOW_CONVERSION = {
  'lbs/h': 1,
  'kg/h': 0.453592,
};

const average = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr.reduce((sum, v) => sum + v, 0) / arr.length;
};

const computeMinResolution = (timeframe) => {
  if (timeframe === '7d') return '1h';
  if (timeframe === '31d') return '1h';
  if (timeframe === '365d') return '1d';
  return '1h';
};

const Boiler = props => {
  const [showModal, setShowModal] = useState(false);
  const [boilerConfig, setBoilerConfig] = useState(null);
  const [boilerMetrics, setBoilerMetrics] = useState(null);
  const [siteID, setSiteID] = useState('');
  const [tabValue, setTabValue] = useState('graph');
  const [panelChecked, setPanelChecked] = useState({});
  const [boilerPanelFlags, setBoilerPanelFlags] = useState({ disabledFields: [] });
  const [unitLimitTrigger, setUnitLimitTrigger] = useState(0);
  const [useExcel, setUseExcel] = useState(true);
  const [summaryPeriod, setSummaryPeriod] = useState('hourly');
  const [energyUnit, setEnergyUnit] = useState('MBTU');
  const [massFlowUnit, setMassFlowUnit] = useState('lbs/h');
  const [loading, setLoading] = useState(true);

  const [plotSpecs, setPlotSpecs] = useState({
    timeframe: '7d',
    resolution: '1h',
    customStart: '',
    customEnd: '',
  });

  const [selectedDates, setSelectedDates] = useState({
    start: null,
    end: null,
    startTzName: '',
    endTzName: '',
    tzName: 'America/New_York',
    refreshReady: false,
  });

  const handleDownload = () => {
    let sliceSize = 1024;
    let byteCharacters = atob(BOILER_TEMP);
    let bytesLength = byteCharacters.length;
    let slicesCount = Math.ceil(bytesLength / sliceSize);
    let byteArrays = new Array(slicesCount);
  
    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);
      let bytes = new Array(end - begin);
  
      for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
  
    FileSaver.saveAs(
      new Blob(byteArrays, { type: "application/vnd.ms-excel" }),
      "boilertemplate.xlsx"
    );
  }

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    // Strip the "data:<mime>;base64," prefix so only the raw base64 is sent
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const excelData = await readFileAsBase64(file);
      await axiosProtect.post('/b/boiler/add-excel-data', {
        site_id: siteID,
        file_name: file.name,
        excel_data: excelData,
      });
    } catch (error) {
      console.log(error);
    } finally {
      e.target.value = '';
    }
  };

  const handlePanelChange = (key, value) => {
    if (value) {
      const newUnit = KEY_UNIT_MAP[key];
      const activeUnits = new Set(
        Object.entries(panelChecked)
          .filter(([, v]) => v)
          .map(([k]) => KEY_UNIT_MAP[k])
      );
      if (newUnit && !activeUnits.has(newUnit) && activeUnits.size >= 2) {
        setUnitLimitTrigger(t => t + 1);
        return;
      }
    }
    setPanelChecked(prev => ({ ...prev, [key]: value }));
  };


  const updatePlotSpecs = (e, field) => {
    if (field === 'timeframe') {
      const val = e.target.value;
      if (val === 'custom') {
        setPlotSpecs(prev => ({
          ...prev,
          timeframe: 'custom',
          customStart: selectedDates.start ? selectedDates.start.unix() : '',
          customEnd: selectedDates.end ? selectedDates.end.unix() : '',
        }));
      } else {
        setPlotSpecs(prev => ({
          ...prev,
          timeframe: val,
          resolution: computeMinResolution(val),
          customStart: '',
          customEnd: '',
        }));
      }
      setSelectedDates(prev => ({ ...prev, refreshReady: false }));
    } else if (field === 'resolution') {
      setPlotSpecs(prev => ({ ...prev, resolution: e.target.value }));
    }
  };

  const axiosProtect = useAxiosProtect();

  useEffect(() => {
    const controller = new AbortController();
    let site_id = parseInt(window.location.href.split('/').slice(-1));
    if (site_id) setSiteID(site_id);

    const getBoilerData = async () => {
      setLoading(true);
      try {
        let url = `/b/boiler/calculate-boiler?site_id=${site_id}&useExcel=${useExcel}`;
        if (plotSpecs.timeframe === 'custom' && plotSpecs.customStart && plotSpecs.customEnd) {
          url += `&start=${plotSpecs.customStart}&end=${plotSpecs.customEnd}`;
        } else {
          url += `&timeframe=${plotSpecs.timeframe}`;
        }
        url += `&resolution=${plotSpecs.resolution}`;

        const response = await axiosProtect.get(url, { signal: controller.signal });
        const { boiler, ...metrics } = response.data || {};
        if (boiler) {
          setBoilerConfig(mapApiBoilerToConfig(boiler));
          setBoilerPanelFlags(computeBoilerPanelFlags(boiler));
        }
        console.log(metrics);
        setBoilerMetrics(metrics);
      } catch (err) {
        console.log(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    getBoilerData();

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useExcel, plotSpecs.timeframe, plotSpecs.resolution, plotSpecs.customStart, plotSpecs.customEnd]);

  const imageKey = getPFDImageKey(boilerConfig);
  let imageSrc = boilerConfig?.diagram || boilerConfig?.diagramImage || null;
  if (!imageSrc && imageKey) {
    try {
      imageSrc = require(`../../../../media/boiler-diagrams/${imageKey}.png`);
    } catch {
      imageSrc = null;
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Button variant="contained" onClick={() => setShowModal(true)}>
          {boilerConfig ? 'Edit Boiler' : 'Add Boiler'}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography>{useExcel ? 'Excel' : 'Timestream'}</Typography>
          <Switch checked={useExcel} onChange={e => setUseExcel(e.target.checked)} disabled={loading} />
        </Box>
      </Box>
      <>
        <Box sx={{ width: '100%', backgroundColor: 'rgba(255,255,255,0.4)' }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'rgb(0,177,168)',
              },
              '& .MuiTab-textColorSecondary.Mui-selected': {
                color: 'rgb(0,177,168)',
              },
              '& .MuiTab-textColorSecondary': {
                color: 'rgb(8,114,182)',
              },
            }}
            aria-label="secondary tabs example"
          >
            <Tab value="graph" label="Graph" />
            <Tab value="schematic" label="Schematic" />
          </Tabs>
        </Box>
        <br />
        {tabValue === 'schematic' ? (
          imageSrc ? (
            <Box mt={2} sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2, minWidth: 220, backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Select
                    value={summaryPeriod}
                    onChange={e => setSummaryPeriod(e.target.value)}
                    size="small"
                    variant="standard"
                    disableUnderline
                    sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                  >
                    <MenuItem value="hourly">Hourly</MenuItem>
                    <MenuItem value="yearly">Daily</MenuItem>
                  </Select>
                  <Typography fontWeight="bold">Steam Plant Summary</Typography>
                </Box>
                {SUMMARY_FIELDS.map(({ label, key, percentage, value, unit }) => {
                  const conversionFactor = unit === 'MBTU'
                    ? ENERGY_CONVERSION[energyUnit]
                    : unit === 'lbs'
                      ? MASS_FLOW_CONVERSION[massFlowUnit]
                      : 1;

                  let displayValue;
                  if (value !== undefined) {
                    displayValue = (value * conversionFactor).toFixed(2);
                  } else {
                    const avg = average(boilerMetrics?.[key]);
                    displayValue = avg === null
                      ? '—'
                      : percentage
                        ? `${(avg * 100).toFixed(1)}%`
                        : (avg * conversionFactor).toFixed(2);
                  }

                  let unitEl = null;
                  if (unit === 'MBTU') {
                    unitEl = (
                      <Select
                        value={energyUnit}
                        onChange={e => setEnergyUnit(e.target.value)}
                        size="small"
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}
                      >
                        <MenuItem value="MBTU">MBTU</MenuItem>
                        <MenuItem value="BTU">BTU</MenuItem>
                        <MenuItem value="MMBTU">MMBTU</MenuItem>
                        <MenuItem value="kWh">kWh</MenuItem>
                      </Select>
                    );
                  } else if (unit === 'lbs') {
                    unitEl = (
                      <Select
                        value={massFlowUnit}
                        onChange={e => setMassFlowUnit(e.target.value)}
                        size="small"
                        variant="standard"
                        disableUnderline
                        sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}
                      >
                        <MenuItem value="lbs/h">lbs/h</MenuItem>
                        <MenuItem value="kg/h">kg/h</MenuItem>
                      </Select>
                    );
                  }

                  return (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5, borderBottom: '1px solid #eee' }}>
                      <Typography variant="body2">{label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{displayValue}</Typography>
                        {unitEl}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <img
                src={imageSrc}
                alt={`PFD Diagram: ${imageKey}`}
                style={{ maxWidth: '70%', border: '1px solid #ccc', borderRadius: 4 }}
              />
            </Box>
          ) : (
            <Typography mt={2} color="text.secondary">
              {boilerConfig ? 'No diagram available for this configuration.' : 'Configure a boiler to view its schematic.'}
            </Typography>
          )
        ) : (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <BoilerPanel
              checked={panelChecked}
              onChange={handlePanelChange}
              disabledFields={boilerPanelFlags.disabledFields}
              disabled={loading}
            />
            <Box sx={{ flex: 1 }}>
              <BoilerToolbar
                plotSpecs={plotSpecs}
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                updatePlotSpecs={updatePlotSpecs}
                disabled={loading}
              />
              {boilerConfig || loading ? (
                <BoilerGraph metrics={boilerMetrics} checked={panelChecked} unitLimitTrigger={unitLimitTrigger} loading={loading} />
              ) : (
                <Typography mt={2} color="text.secondary">
                  Configure a boiler to view the graph.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </>
      <div style={{display:'flex', flexDirection:'row'}}>
        <div>
          <small>First:</small>
          <br/>
          <Button style={{ marginBottom: '2%' }} onClick={handleDownload}>Download Boiler Template</Button>
        </div>

        <div style={{width:'2vw'}}></div>

        <div>
          <small>Then:</small>
          <br/>
          <Button style={{ marginBottom: '2%' }} onClick={() => document.getElementById('file-upload').click()}>Upload Boiler Template</Button>
          <input style={{display: "none"}} id="file-upload" type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
        </div>
      </div>

      <BoilerModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={setBoilerConfig}
        initialConfig={boilerConfig}
      />
    </Box>
  );
};

export default Boiler;
